import { Button, Col, Divider, Row, message } from "antd";
import { useContext, useEffect, useMemo, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import ShelfContext from "../../../../context/wms/shelf/context";
import ShelfContextProvider from "../../../../context/wms/shelf/provider";
import { ICreateShelf } from "../../../../models/wms/shelf/request";
import { AisleLayout, PlacementSide } from "../../../../models/wms/enums";
import EndPoints from "../../../../services/end-points";
import { execute } from "../../../../utils/helpers/execute";
import FieldBuilder from "../../../form-components/field-builder";
import Spin from "../../../general/antd/spin";
import styles from "./style.module.scss";
import useHierarchicalDropdown, { ChainItem } from "../../../../hooks/wms/use-hierarchical-dropdown";
import PreviousChainDisplay from "../../shared/previous-chain-display";

const Form = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { details, actions, loading } = useContext(ShelfContext);
  const { id } = useParams();

  const methods = useForm<ICreateShelf>({
    defaultValues: {
      storageUnitId: details?.storageUnitId || undefined,
      hasBins: details?.hasBins || false,
      pickSequence: details?.pickSequence || undefined,
      isActive: details?.isActive || false,
    },
  });

  const {
    handleSubmit,
    reset,
    setValue,
    watch,
  } = methods;

  useEffect(() => {
    id && actions.getDetails(Number(id));
  }, [id]);

  const [loadingSubmit, setLoadingSubmit] = useState(false);

  // Use hierarchical dropdown hook with progressive visibility
  const {
    stores,
    loadingStores,
    zoneTypes,
    loadingZoneTypes,
    zones,
    loadingZones,
    aisles,
    loadingAisles,
    storageUnits,
    loadingStorageUnits,
    visibleLevels,
    previousChain,
    capturePreviousChain,
    clearPreviousChain,
    resetFromLevel,
    selectedStoreId,
    selectedZoneTypeId,
    selectedZoneId,
    selectedAisleId,
    selectedAisleLayout,
    selectedStorageUnitId,
    selectedSidesFilter,
    selectedSideFilter,
    setSelectedStoreId,
    setSelectedZoneTypeId,
    setSelectedZoneId,
    setSelectedAisleId,
    setSelectedStorageUnitId,
    setSelectedSidesFilter,
    setSelectedSideFilter,
    initializeForEdit,
  } = useHierarchicalDropdown({ debounceMs: 300, shelvesWithBinsOnly: false });

  // Watch form values for ancestor selectors
  const formStoreId = watch("storeId" as any);
  const formZoneTypeId = watch("zoneTypeId" as any);
  const formZoneId = watch("zoneId" as any);
  const formAisleId = watch("aisleId" as any);

  // Sync form values with hook selected values
  useEffect(() => {
    if (formStoreId !== selectedStoreId) {
      setSelectedStoreId(formStoreId);
    }
  }, [formStoreId, setSelectedStoreId]);

  useEffect(() => {
    if (formZoneTypeId !== selectedZoneTypeId) {
      setSelectedZoneTypeId(formZoneTypeId);
    }
  }, [formZoneTypeId, setSelectedZoneTypeId]);

  useEffect(() => {
    if (formZoneId !== selectedZoneId) {
      setSelectedZoneId(formZoneId);
    }
  }, [formZoneId, setSelectedZoneId]);

  useEffect(() => {
    if (formAisleId !== selectedAisleId) {
      setSelectedAisleId(formAisleId);
    }
  }, [formAisleId, setSelectedAisleId]);

  // Derive valid side options from selected aisle's layout
  const sideFilterOptions = useMemo(() => {
    const allOptions = [
      { label: t("left_side"), value: PlacementSide.L },
      { label: t("right_side"), value: PlacementSide.R },
    ];
    if (!selectedAisleLayout) return allOptions;
    if (selectedAisleLayout === AisleLayout.LEFT) return [{ label: t("left_side"), value: PlacementSide.L }];
    if (selectedAisleLayout === AisleLayout.RIGHT) return [{ label: t("right_side"), value: PlacementSide.R }];
    return allOptions; // BOTH
  }, [selectedAisleLayout, t]);

  // Auto-reset side filter when aisle changes and current value becomes invalid
  useEffect(() => {
    if (sideFilterOptions.length === 1) {
      setSelectedSideFilter(sideFilterOptions[0].value);
    }
  }, [sideFilterOptions, setSelectedSideFilter]);

  // Helper to get translated label for sides value
  const getSidesLabel = (sides: number | undefined): string => {
    if (sides === undefined) return '';
    if (sides === AisleLayout.LEFT) return t('left_side');
    if (sides === AisleLayout.RIGHT) return t('right_side');
    if (sides === AisleLayout.BOTH) return t('both_sides');
    return `${sides}`;
  };

  // Helper to get translated label for side value
  const getSideLabel = (side: number | undefined): string => {
    if (side === undefined) return '';
    if (side === PlacementSide.R) return t('right_side');
    if (side === PlacementSide.L) return t('left_side');
    return `${side}`;
  };

  // Capture previous chain on mount in edit mode
  useEffect(() => {
    if (id && details) {
      const chain: ChainItem[] = [];
      if (details.store?.name) {
        chain.push({ label: details.store.name, value: details.storeId });
      } else if (details.storeId) {
        chain.push({ label: `Store ${details.storeId}`, value: details.storeId });
      }
      if (details.zone?.name) {
        chain.push({ label: details.zone.name, value: details.zoneId });
      } else if (details.zoneId) {
        chain.push({ label: `Zone ${details.zoneId}`, value: details.zoneId });
      }
      if (details.aisle?.name) {
        const aisleLabel = details.aisle.sides !== undefined
          ? `${details.aisle.name} - ${t('sides')}: ${getSidesLabel(details.aisle.sides)}`
          : details.aisle.name;
        chain.push({ label: aisleLabel, value: details.aisleId });
      } else if (details.aisleId) {
        chain.push({ label: `Aisle ${details.aisleId}`, value: details.aisleId });
      }
      if (details.storageUnit?.name) {
        const suLabel = details.storageUnit.side !== undefined
          ? `${details.storageUnit.name} - ${t('side')}: ${getSideLabel(details.storageUnit.side)}`
          : details.storageUnit.name;
        chain.push({ label: suLabel, value: details.storageUnitId });
      } else if (details.storageUnitId) {
        chain.push({ label: `Storage Unit ${details.storageUnitId}`, value: details.storageUnitId });
      }
      if (chain.length > 0) {
        capturePreviousChain(chain);
      }
    }
  }, [id, details]);

  // Handle store change - reset children
  const handleStoreChange = (storeId: number | undefined) => {
    if (storeId && storeId !== details?.storeId) {
      resetFromLevel('store');
      setValue('zoneId' as any, undefined);
      setValue('aisleId' as any, undefined);
      setValue('storageUnitId' as any, undefined);
    }
  };

  // Handle zone type change - reset children
  const handleZoneTypeChange = (zoneTypeId: number | undefined) => {
    if (zoneTypeId && zoneTypeId !== details?.zone?.zoneTypeId) {
      resetFromLevel('zone');
      setValue('zoneId' as any, undefined);
      setValue('aisleId' as any, undefined);
      setValue('storageUnitId' as any, undefined);
    }
  };

  // Handle zone change - reset children
  const handleZoneChange = (zoneId: number | undefined) => {
    if (zoneId && zoneId !== details?.zoneId) {
      resetFromLevel('zone');
      setValue('aisleId' as any, undefined);
      setValue('storageUnitId' as any, undefined);
    }
  };

  // Handle aisle change - reset children
  const handleAisleChange = (aisleId: number | undefined) => {
    if (aisleId && aisleId !== details?.aisleId) {
      resetFromLevel('aisle');
      setValue('storageUnitId' as any, undefined);
    }
  };

  // When details load, reset form and initialize hierarchical dropdowns immediately
  useEffect(() => {
    if (details) {
      reset({
        storeId: details.storeId ?? undefined,
        zoneTypeId: details.zone?.zoneTypeId ?? undefined,
        zoneId: details.zoneId ?? undefined,
        helperAisleSides: details.aisle?.sides ?? undefined,
        aisleId: details.aisleId ?? undefined,
        helperStorageUnitSide: details.storageUnit?.side ?? undefined,
        storageUnitId: details.storageUnitId ?? undefined,
        hasBins: details.hasBins ?? false,
        pickSequence: details.pickSequence ?? undefined,
        isActive: details.isActive ?? false,
      } as any);
      initializeForEdit({
        storeId: details.storeId,
        zoneTypeId: details.zone?.zoneTypeId,
        zoneId: details.zoneId,
        sidesFilter: details.aisle?.sides,
        aisleId: details.aisleId,
        sideFilter: details.storageUnit?.side,
        storageUnitId: details.storageUnitId,
      });
    }
  }, [details, reset, initializeForEdit]);

  const normalizePickSequence = (v: unknown): number | undefined => {
    if (v === "" || v === undefined || v === null) return undefined;
    const n = typeof v === "number" ? v : Number(v);
    if (!Number.isFinite(n) || n < 1) return undefined;
    return n;
  };

  const onSubmit = async (data: ICreateShelf) => {
    const {
      storeId,
      zoneTypeId,
      zoneId,
      aisleId,
      helperAisleSides,
      helperStorageUnitSide,
      pickSequence,
      ...rest
    } = data as any;
    const pickSeq = normalizePickSequence(pickSequence);
    const submitData = {
      ...rest,
      ...(pickSeq !== undefined ? { pickSequence: pickSeq } : {}),
    };
    await execute({
      callback: async () => {
        setLoadingSubmit(true);
        if (details) {
          await EndPoints.wmsLayout.updateShelf(details.id, submitData);
        } else {
          await EndPoints.wmsLayout.createShelf(submitData);
        }
        navigate("/wms/shelves");
      },
      fallback: (error) => {
        message.error(t('operation_failed'));
      },
      finallyCallback: () => {
        setLoadingSubmit(false);
      },
      throwException: false,
    });
  };

  return (
    <FormProvider {...methods}>
      <form className={styles.container} onSubmit={handleSubmit(onSubmit)}>
        <div>
          <span className={styles.title}>
            {details ? t("update_shelf") : t("add_shelf")}
          </span>
          <Divider className={styles.divider} />
          {loading.includes("details") && id ? (
            <div style={{ height: "60vh" }}>
              <Spin />
            </div>
          ) : (
            <>
              {previousChain && (
                <PreviousChainDisplay
                  chain={previousChain}
                  onClear={clearPreviousChain}
                />
              )}
              <div className={styles.children}>
                <Row gutter={[16, 16]}>
                  <Col xs={24} md={12}>
                    <FieldBuilder
                      name="storeId"
                      label={t("select_store")}
                      input={{
                        type: "select",
                        loading: loadingStores,
                        allowSearch: true,
                        options: stores.map((s) => ({
                          label: s.label,
                          value: s.id,
                        })),
                        onChange: handleStoreChange,
                      }}
                      rules={{ required: !details }}
                    />
                  </Col>
                  <Col xs={24} md={12}>
                    <FieldBuilder
                      name="zoneTypeId"
                      label={t("zone_type")}
                      input={{
                        type: "select",
                        loading: loadingZoneTypes,
                        allowSearch: true,
                        options: zoneTypes.map((zt) => ({
                          label: zt.label,
                          value: zt.id,
                        })),
                        onChange: handleZoneTypeChange,
                      }}
                      rules={{ required: false }}
                    />
                  </Col>
                  {visibleLevels.zone && (
                    <Col xs={24} md={12}>
                      <FieldBuilder
                        name="zoneId"
                        label={t("select_zone")}
                        input={{
                          type: "select",
                          loading: loadingZones,
                          allowSearch: true,
                          options: zones.map((z) => ({
                            label: z.label,
                            value: z.id,
                          })),
                          onChange: handleZoneChange,
                        }}
                        rules={{ required: !details }}
                      />
                    </Col>
                  )}
                  {visibleLevels.aisle && (
                      <Col xs={24} md={12}>
                        <FieldBuilder
                            name="helperAisleSides"
                            label={`${t("available_sides")}`}
                            input={{
                              type: "select",
                              options: [
                                { label: t("left_side"), value: 1 },
                                { label: t("right_side"), value: 2 },
                                { label: t("both_sides"), value: 3 },
                              ],
                              onChange: setSelectedSidesFilter,
                            }}
                            rules={{ required: false }}
                        />
                      </Col>
                  )}
                  {visibleLevels.aisle && (
                    <Col xs={24} md={12}>
                      <FieldBuilder
                        name="aisleId"
                        label={t("select_aisle")}
                        input={{
                          type: "select",
                          loading: loadingAisles,
                          allowSearch: true,
                          options: aisles.map((a) => ({
                            label: a.label,
                            value: a.id,
                          })),
                          onChange: handleAisleChange,
                        }}
                        rules={{ required: !details }}
                      />
                    </Col>
                  )}
                  {visibleLevels.storageUnit && (
                      <Col xs={24} md={12}>
                        <FieldBuilder
                            name="helperStorageUnitSide"
                            label={`${t("placement_side")}`}
                            input={{
                              type: "select",
                              options: sideFilterOptions,
                              onChange: setSelectedSideFilter,
                            }}
                            rules={{ required: false }}
                        />
                      </Col>
                  )}
                  {visibleLevels.storageUnit && (
                    <Col xs={24} md={12}>
                      <FieldBuilder
                        name="storageUnitId"
                        label={t("select_storage_unit")}
                        input={{
                          type: "select",
                          loading: loadingStorageUnits,
                          allowSearch: true,
                          options: storageUnits.map((su) => ({
                            label: su.label,
                            value: su.id,
                          })),
                        }}
                        rules={{ required: true }}
                      />
                    </Col>
                  )}
                  <Col xs={24} md={12}>
                    <FieldBuilder
                      name="hasBins"
                      label={t("has_bins")}
                      input={{ type: "switch" }}
                      rules={{ required: false }}
                    />
                  </Col>
                  <Col xs={24} md={12}>
                    <FieldBuilder
                      name="pickSequence"
                      label={t("pick_sequence")}
                      input={{ type: "number" }}
                      rules={{
                        required: false,
                        validate: (v) =>
                          v === "" || v === undefined || v === null || Number(v) >= 1
                            ? true
                            : t("min", { input: t("pick_sequence"), value: 1 }),
                      }}
                    />
                  </Col>
                  <Col xs={24} md={12}>
                    <FieldBuilder
                      name="isActive"
                      label={t("is_active")}
                      input={{ type: "switch" }}
                      rules={{ required: false }}
                    />
                  </Col>
                </Row>
              </div>
            </>
          )}
        </div>

        <div>
          <Divider className={styles.divider} />
          <div className={styles.formFooter}>
            <Button
              onClick={() => {
                navigate(-1);
              }}
            >
              {t("cancel")}
            </Button>
            <Button loading={loadingSubmit} htmlType="submit" type="primary">
              {t("save")}
            </Button>
          </div>
        </div>
      </form>
    </FormProvider>
  );
};

const ShelfForm = () => {
  return (
    <ShelfContextProvider>
      <Form />
    </ShelfContextProvider>
  );
};

export default ShelfForm;
